import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";

const ORBIT_MIN_POLAR_ANGLE=.18;
const ORBIT_MAX_POLAR_ANGLE=Math.PI*.47;

class HorizontalWalkControls extends THREE.EventDispatcher {
  constructor(camera, canvas) {
    super();
    this.object=camera; this.domElement=canvas; this.enabled=true; this.isLocked=false; this.rightButtonDown=false;
    this.yawSensitivity=.0008; this.maxYawMovement=20; this.pitchSensitivity=.00045; this.minPitch=-.42; this.maxPitch=.42;
    this.moveVector=new THREE.Vector3();
    this.document=canvas.ownerDocument;
    this.onMouseMove=this.onMouseMove.bind(this); this.onMouseDown=this.onMouseDown.bind(this); this.onMouseUp=this.onMouseUp.bind(this);
    this.onPointerLockChange=this.onPointerLockChange.bind(this); this.onPointerLockError=this.onPointerLockError.bind(this);
    this.document.addEventListener("mousemove",this.onMouseMove);
    this.document.addEventListener("mousedown",this.onMouseDown);
    this.document.addEventListener("mouseup",this.onMouseUp);
    this.document.addEventListener("pointerlockchange",this.onPointerLockChange);
    this.document.addEventListener("pointerlockerror",this.onPointerLockError);
  }

  onMouseMove(event) {
    if(!this.isLocked||!this.enabled)return;
    const rotation=this.object.rotation; rotation.order="YXZ";
    const horizontalMovement=THREE.MathUtils.clamp(event.movementX,-this.maxYawMovement,this.maxYawMovement);
    rotation.y-=horizontalMovement*this.yawSensitivity;
    if(this.rightButtonDown||(event.buttons&2)===2){
      rotation.x=THREE.MathUtils.clamp(rotation.x-event.movementY*this.pitchSensitivity,this.minPitch,this.maxPitch);
    }
    rotation.z=0;
    this.dispatchEvent({type:"change"});
  }

  onMouseDown(event) {if(this.isLocked&&event.button===2){this.rightButtonDown=true;event.preventDefault();}}
  onMouseUp(event) {if(event.button===2)this.rightButtonDown=false;}
  onPointerLockChange() {
    const locked=this.document.pointerLockElement===this.domElement;
    if(locked===this.isLocked)return;
    this.isLocked=locked; this.rightButtonDown=false; this.dispatchEvent({type:locked?"lock":"unlock"});
  }
  onPointerLockError() {console.error("無法鎖定滑鼠，請重新點擊 3D 畫面。");}
  lock() {this.domElement.requestPointerLock();}
  unlock() {this.document.exitPointerLock();}
  moveForward(distance) {
    if(!this.enabled)return;
    this.moveVector.setFromMatrixColumn(this.object.matrix,0).crossVectors(this.object.up,this.moveVector);
    this.object.position.addScaledVector(this.moveVector,distance);
  }
  moveRight(distance) {
    if(!this.enabled)return;
    this.moveVector.setFromMatrixColumn(this.object.matrix,0);
    this.object.position.addScaledVector(this.moveVector,distance);
  }
}

export class SceneControls {
  constructor(camera, canvas, chapters, onModeChange) {
    this.camera=camera; this.canvas=canvas; this.chapters=chapters; this.mode="overview"; this.onModeChange=onModeChange;
    this.orbit=new OrbitControls(camera,canvas); this.orbit.enableDamping=true; this.orbit.dampingFactor=.06; this.orbit.rotateSpeed=.24; this.orbit.target.set(0,0,4); this.orbit.minPolarAngle=ORBIT_MIN_POLAR_ANGLE; this.orbit.maxPolarAngle=ORBIT_MAX_POLAR_ANGLE; this.orbit.minDistance=22; this.orbit.maxDistance=210; this.orbit.mouseButtons.RIGHT=THREE.MOUSE.ROTATE;
    this.walk=new HorizontalWalkControls(camera,canvas); this.keys=new Set(); this.velocity=new THREE.Vector3(); this.tween=null;
    addEventListener("keydown",e=>this.keys.add(e.code)); addEventListener("keyup",e=>this.keys.delete(e.code));
    this.walk.addEventListener("lock",()=>this.onModeChange?.("walk-locked")); this.walk.addEventListener("unlock",()=>this.onModeChange?.("walk-unlocked"));
    canvas.addEventListener("contextmenu",e=>{if(this.mode==="overview"||this.mode==="walk")e.preventDefault();});
    canvas.addEventListener("pointerdown",e=>{
      if(this.mode!=="overview"||e.pointerType!=="mouse")return;
      if(e.button===2){this.orbit.minPolarAngle=ORBIT_MIN_POLAR_ANGLE;this.orbit.maxPolarAngle=ORBIT_MAX_POLAR_ANGLE;this.orbit.rotateSpeed=.16;}
      if(e.button===0){const angle=this.orbit.getPolarAngle();this.orbit.minPolarAngle=angle;this.orbit.maxPolarAngle=angle;this.orbit.rotateSpeed=.24;}
    });
    canvas.ownerDocument.addEventListener("pointerup",e=>{
      if(e.button!==0&&e.button!==2)return;
      this.orbit.minPolarAngle=ORBIT_MIN_POLAR_ANGLE;this.orbit.maxPolarAngle=ORBIT_MAX_POLAR_ANGLE;this.orbit.rotateSpeed=.24;
    });
  }

  setMode(mode, lockNow=false) {
    this.mode=mode; this.tween=null; this.orbit.enabled=mode==="overview";
    if(mode!=="walk"&&this.walk.isLocked)this.walk.unlock();
    if(mode==="overview")this.focus(0,true);
    if(mode==="walk"){
      this.orbit.enabled=false; this.camera.position.set(0,1.72,68); this.camera.rotation.set(0,0,0,"YXZ");
      if(lockNow)this.walk.lock();
    }
    if(mode==="tour"){this.orbit.enabled=false;this.focus(0,true);}
    this.onModeChange?.(mode);
  }

  focus(index, immediate=false) {
    const chapter=this.chapters[index]; if(!chapter)return;
    const from=this.camera.position.clone(), to=new THREE.Vector3(...chapter.position), target=new THREE.Vector3(...chapter.target);
    if(immediate){this.tween=null;this.camera.position.copy(to);this.camera.lookAt(target);this.orbit.target.copy(target);return;}
    const startTarget=this.orbit.target.clone();this.orbit.enabled=false;this.tween={from,to,startTarget,target,elapsed:0,duration:1.35};
  }

  update(delta) {
    if(this.tween){const t=this.tween;t.elapsed+=delta;const p=Math.min(t.elapsed/t.duration,1),e=1-Math.pow(1-p,3);this.camera.position.lerpVectors(t.from,t.to,e);const look=t.startTarget.clone().lerp(t.target,e);this.camera.lookAt(look);this.orbit.target.copy(look);if(p===1){this.tween=null;this.orbit.enabled=this.mode==="overview";}}
    if(this.mode==="overview"&&!this.tween)this.orbit.update();
    if(this.mode==="walk"&&this.walk.isLocked){
      const speed=(this.keys.has("ShiftLeft")||this.keys.has("ShiftRight"))?13:7;
      let forward=0,right=0;if(this.keys.has("KeyW")||this.keys.has("ArrowUp"))forward+=1;if(this.keys.has("KeyS")||this.keys.has("ArrowDown"))forward-=1;if(this.keys.has("KeyD")||this.keys.has("ArrowRight"))right+=1;if(this.keys.has("KeyA")||this.keys.has("ArrowLeft"))right-=1;
      const length=Math.hypot(forward,right)||1;const before=this.camera.position.clone();this.walk.moveForward(forward/length*speed*delta);this.walk.moveRight(right/length*speed*delta);
      const p=this.camera.position;p.y=1.72;const maxX=68-Math.max(0,p.z-40)*.22;if(Math.abs(p.x)>maxX||p.z>70||p.z<-63)p.copy(before);
    }
  }
}
